import { Command } from 'commander';
import Table from 'cli-table3';
import chalk from 'chalk';
import inquirer from 'inquirer';
import ora from 'ora';
import { AgentManager } from '../core/agent-manager';
import { 
  AgentCollaborationManager, 
  COLLABORATION_STRATEGIES,
  CollaborationAgent
} from '../core/agent-collaboration';
import { TaskStore } from '../core/task-store';
import { HookManager } from '../core/hooks/hook-manager';
import { logger } from '../utils/logger';

export function createCollaborateCommand(): Command {
  const command = new Command('collaborate');
  
  command
    .description('管理多 Agent 协作任务')
    .alias('collab')
    .addCommand(createStartCommand())
    .addCommand(createListCommand())
    .addCommand(createStatusCommand())
    .addCommand(createStrategiesCommand())
    .addCommand(createCustomCommand());
  
  return command;
}

function createStartCommand(): Command {
  const command = new Command('start');
  
  command
    .description('启动 Agent 协作任务')
    .argument('[task]', '任务描述')
    .option('-s, --strategy <strategy>', '使用指定的协作策略')
    .option('-n, --name <name>', '协作任务名称')
    .option('-c, --context <json>', '项目上下文 (JSON)')
    .action(async (task, options) => {
      try {
        // 初始化管理器
        const agentManager = new AgentManager();
        await agentManager.initialize();
        
        const taskStore = new TaskStore();
        const hookManager = new HookManager();
        const collaborationManager = new AgentCollaborationManager(
          agentManager,
          taskStore,
          hookManager
        );
        
        // 如果没有提供任务，交互式输入
        if (!task) {
          const { taskInput } = await inquirer.prompt({
            type: 'input',
            name: 'taskInput',
            message: '请描述需要协作完成的任务:',
            validate: (input) => input.trim() !== ''
          });
          task = taskInput;
        }
        
        // 选择协作策略
        let strategy = options.strategy;
        if (!strategy) {
          // 尝试推荐策略
          const mockTask = { id: 'temp', content: task, status: 'todo', priority: 'normal' } as any;
          const recommended = collaborationManager.recommendStrategy(mockTask);
          
          const strategies = Object.entries(COLLABORATION_STRATEGIES).map(([key, value]) => ({
            name: `${value.name} - ${value.description}`,
            value: key,
            short: value.name
          }));
          
          if (recommended) {
            strategies.unshift({
              name: chalk.green(`[推荐] ${recommended.name} - ${recommended.description}`),
              value: Object.keys(COLLABORATION_STRATEGIES).find(
                k => COLLABORATION_STRATEGIES[k] === recommended
              )!,
              short: recommended.name
            });
          }
          
          const { selectedStrategy } = await inquirer.prompt({
            type: 'list',
            name: 'selectedStrategy',
            message: '选择协作策略:',
            choices: strategies
          });
          
          strategy = selectedStrategy;
        }
        
        // 解析上下文
        let context = {};
        if (options.context) {
          try {
            context = JSON.parse(options.context);
          } catch {
            logger.warn('Invalid context JSON, using empty context');
          }
        }
        
        // 创建协作任务
        const name = options.name || `协作任务: ${task.substring(0, 50)}`;
        const collaboration = await collaborationManager.createCollaboration(
          name,
          task,
          strategy,
          context
        );
        
        console.log(chalk.green(`\n✅ 创建协作任务: ${collaboration.name}`));
        console.log(`ID: ${collaboration.id}`);
        console.log(`策略: ${COLLABORATION_STRATEGIES[strategy].name}`);
        console.log(`Agent 数量: ${collaboration.agents.length}`);
        
        // 显示执行计划
        console.log('\n' + chalk.bold('执行计划:'));
        const planTable = new Table({
          head: ['顺序', '角色', 'Agent', '任务'],
          style: { head: ['cyan'] }
        });
        
        collaboration.agents.forEach((agent, index) => {
          const agentInfo = agentManager.getAgent(agent.agentId);
          planTable.push([
            collaboration.type === 'sequential' ? index + 1 : '-',
            agent.role,
            agentInfo?.name || agent.agentId,
            agent.task
          ]);
        });
        
        console.log(planTable.toString());
        
        // 确认执行
        const { confirm } = await inquirer.prompt({
          type: 'confirm',
          name: 'confirm',
          message: '是否立即执行协作任务？',
          default: true
        });
        
        if (!confirm) {
          console.log(chalk.yellow('协作任务已创建但未执行'));
          console.log(`使用 "workflow collaborate status ${collaboration.id}" 查看状态`);
          return;
        }
        
        // 执行协作
        const spinner = ora('执行协作任务...').start();
        
        collaborationManager.on('agentTaskStarted', ({ agent }) => {
          spinner.text = `执行中: ${agent.role} - ${agent.task}`;
        });
        
        collaborationManager.on('agentTaskCompleted', ({ agent }) => {
          spinner.succeed(`完成: ${agent.role}`);
          spinner.start('继续执行...');
        });
        
        try {
          const results = await collaborationManager.executeCollaboration(collaboration.id);
          spinner.succeed('协作任务完成！');
          
          // 显示结果摘要
          console.log('\n' + collaborationManager.summarizeResults(results));
          
        } catch (error: any) {
          spinner.fail('协作任务失败');
          logger.error(error.message);
        }
        
      } catch (error: any) {
        logger.error('启动协作失败:', error.message);
      }
    });
  
  return command;
}

function createListCommand(): Command {
  const command = new Command('list');
  
  command
    .description('列出所有协作任务')
    .option('-s, --status <status>', '按状态过滤')
    .action(async (options) => {
      try {
        const agentManager = new AgentManager();
        await agentManager.initialize();
        
        const collaborationManager = new AgentCollaborationManager(agentManager);
        const collaborations = collaborationManager.getCollaborations();
        
        // 过滤
        let filtered = collaborations;
        if (options.status) {
          filtered = filtered.filter(c => c.status === options.status);
        }
        
        if (filtered.length === 0) {
          console.log(chalk.yellow('没有找到协作任务'));
          return;
        }
        
        // 显示表格
        const table = new Table({
          head: ['ID', '名称', '策略', '状态', 'Agent数', '创建时间'],
          style: { head: ['cyan'] }
        });
        
        for (const collab of filtered) {
          const strategyName = collab.type === 'sequential' ? '顺序执行' :
                              collab.type === 'parallel' ? '并行执行' : '条件执行';
          
          table.push([
            collab.id.substring(0, 8),
            collab.name.substring(0, 30),
            strategyName,
            collab.status === 'completed' ? chalk.green(collab.status) :
            collab.status === 'running' ? chalk.yellow(collab.status) :
            collab.status === 'failed' ? chalk.red(collab.status) :
            chalk.gray(collab.status),
            collab.agents.length.toString(),
            collab.createdAt.toLocaleString()
          ]);
        }
        
        console.log(table.toString());
        
      } catch (error: any) {
        logger.error('列出协作任务失败:', error.message);
      }
    });
  
  return command;
}

function createStatusCommand(): Command {
  const command = new Command('status');
  
  command
    .description('查看协作任务状态')
    .argument('<collaborationId>', '协作任务 ID')
    .action(async (collaborationId) => {
      try {
        const agentManager = new AgentManager();
        await agentManager.initialize();
        
        const collaborationManager = new AgentCollaborationManager(agentManager);
        const collaboration = collaborationManager.getCollaborationStatus(collaborationId);
        
        if (!collaboration) {
          console.log(chalk.red(`协作任务不存在: ${collaborationId}`));
          return;
        }
        
        // 显示基本信息
        console.log(chalk.bold('\n协作任务信息:'));
        console.log(`ID: ${collaboration.id}`);
        console.log(`名称: ${collaboration.name}`);
        console.log(`描述: ${collaboration.description}`);
        console.log(`类型: ${collaboration.type}`);
        console.log(`状态: ${
          collaboration.status === 'completed' ? chalk.green(collaboration.status) :
          collaboration.status === 'running' ? chalk.yellow(collaboration.status) :
          collaboration.status === 'failed' ? chalk.red(collaboration.status) :
          chalk.gray(collaboration.status)
        }`);
        console.log(`创建时间: ${collaboration.createdAt.toLocaleString()}`);
        
        if (collaboration.completedAt) {
          console.log(`完成时间: ${collaboration.completedAt.toLocaleString()}`);
        }
        
        // 显示 Agent 信息
        console.log('\n' + chalk.bold('参与 Agent:'));
        const agentTable = new Table({
          head: ['角色', 'Agent', '任务', '状态'],
          style: { head: ['cyan'] }
        });
        
        for (const agent of collaboration.agents) {
          const agentInfo = agentManager.getAgent(agent.agentId);
          const result = collaboration.results?.find(r => r.agentId === agent.agentId);
          
          agentTable.push([
            agent.role,
            agentInfo?.name || agent.agentId,
            agent.task,
            result ? (result.result.success ? chalk.green('✅ 完成') : chalk.red('❌ 失败')) : 
                    chalk.gray('待执行')
          ]);
        }
        
        console.log(agentTable.toString());
        
        // 显示结果
        if (collaboration.results && collaboration.results.length > 0) {
          console.log('\n' + collaborationManager.summarizeResults(collaboration.results));
        }
        
      } catch (error: any) {
        logger.error('查看状态失败:', error.message);
      }
    });
  
  return command;
}

function createStrategiesCommand(): Command {
  const command = new Command('strategies');
  
  command
    .description('列出所有可用的协作策略')
    .alias('strats')
    .action(() => {
      console.log(chalk.bold('\n可用的协作策略:\n'));
      
      Object.entries(COLLABORATION_STRATEGIES).forEach(([key, strategy]) => {
        console.log(chalk.cyan(`${key}:`));
        console.log(`  名称: ${strategy.name}`);
        console.log(`  描述: ${strategy.description}`);
        console.log(`  执行方式: ${
          strategy.flow === 'sequential' ? '顺序执行' :
          strategy.flow === 'parallel' ? '并行执行' : '条件执行'
        }`);
        console.log(`  参与 Agent:`);
        
        strategy.agents.forEach(agent => {
          console.log(`    - ${agent.role}: ${agent.task}`);
        });
        
        console.log('');
      });
    });
  
  return command;
}

function createCustomCommand(): Command {
  const command = new Command('custom');
  
  command
    .description('创建自定义协作任务')
    .action(async () => {
      try {
        const agentManager = new AgentManager();
        await agentManager.initialize();
        
        const availableAgents = agentManager.getAgents();
        
        // 基本信息
        const { name, description, type } = await inquirer.prompt([
          {
            type: 'input',
            name: 'name',
            message: '协作任务名称:',
            validate: (input) => input.trim() !== ''
          },
          {
            type: 'input',
            name: 'description',
            message: '任务描述:',
            validate: (input) => input.trim() !== ''
          },
          {
            type: 'list',
            name: 'type',
            message: '执行方式:',
            choices: [
              { name: '顺序执行', value: 'sequential' },
              { name: '并行执行', value: 'parallel' },
              { name: '条件执行', value: 'conditional' }
            ]
          }
        ]);
        
        // 添加 Agent
        const agents: CollaborationAgent[] = [];
        let addMore = true;
        
        while (addMore) {
          console.log(chalk.cyan(`\n添加第 ${agents.length + 1} 个 Agent:`));
          
          const { agentId, role, task, required } = await inquirer.prompt([
            {
              type: 'list',
              name: 'agentId',
              message: '选择 Agent:',
              choices: availableAgents.map(a => ({
                name: `${a.name} - ${a.description}`,
                value: a.id
              }))
            },
            {
              type: 'input',
              name: 'role',
              message: '角色名称:',
              default: (answers: any) => {
                const agent = availableAgents.find(a => a.id === answers.agentId);
                return agent?.category || 'agent';
              }
            },
            {
              type: 'input',
              name: 'task',
              message: '具体任务:',
              validate: (input) => input.trim() !== ''
            },
            {
              type: 'confirm',
              name: 'required',
              message: '是否为必需任务？',
              default: true
            }
          ]);
          
          agents.push({
            agentId,
            role,
            task,
            required,
            order: type === 'sequential' ? agents.length : undefined
          });
          
          const { continueAdding } = await inquirer.prompt({
            type: 'confirm',
            name: 'continueAdding',
            message: '是否继续添加 Agent？',
            default: agents.length < 3
          });
          
          addMore = continueAdding;
        }
        
        // 创建协作任务
        const collaborationManager = new AgentCollaborationManager(agentManager);
        const collaboration = await collaborationManager.createCustomCollaboration(
          name,
          description,
          agents,
          type as any
        );
        
        console.log(chalk.green(`\n✅ 创建自定义协作任务: ${collaboration.name}`));
        console.log(`ID: ${collaboration.id}`);
        console.log(`Agent 数量: ${agents.length}`);
        
        // 询问是否执行
        const { execute } = await inquirer.prompt({
          type: 'confirm',
          name: 'execute',
          message: '是否立即执行？',
          default: true
        });
        
        if (execute) {
          const spinner = ora('执行协作任务...').start();
          
          try {
            const results = await collaborationManager.executeCollaboration(collaboration.id);
            spinner.succeed('协作任务完成！');
            console.log('\n' + collaborationManager.summarizeResults(results));
          } catch (error: any) {
            spinner.fail('协作任务失败');
            logger.error(error.message);
          }
        }
        
      } catch (error: any) {
        logger.error('创建自定义协作失败:', error.message);
      }
    });
  
  return command;
}